import { Select, Card, Typography, Space, Tag, Flex, Button, Input, Checkbox } from "antd"
import { CloseCircleOutlined } from "@ant-design/icons"
import { pricingTypeName } from "../../../common/constants"
import { isWorkareaRecurringEnabled, workareaRepeatLabel } from "./recurringScheduleModel"
import { EventFormState } from "./useEventFormState"
import EventFormScheduleFields from "./EventFormScheduleFields"
import EventFormCrewFields from "./EventFormCrewFields"

const { Text } = Typography

type Props = {
  formState: EventFormState
  hideRepeatSelector?: boolean
  showSelectAllButtons?: boolean
}

const EventFormFields = ({
  formState,
  hideRepeatSelector = false,
  showSelectAllButtons = false,
}: Props) => {
  const {
    formValues,
    setFormValues,
    allWorkareas,
    assignedWorkareas,
    removeWorkarea,
    applyWorkareaSchedule,
  } = formState

  const { selectedWorkareaIds } = formValues

  return (
    <div style={{ marginTop: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <Text style={{ fontSize: "14px", display: "block", marginBottom: "8px" }}>
          Work Areas
        </Text>
        <Card>
          <Select
            mode="multiple"
            showSearch
            placeholder="Search Work Areas"
            style={{ width: "100%", marginBottom: showSelectAllButtons ? 0 : "16px" }}
            options={allWorkareas.map((workarea) => {
              const isApproved = workarea.isApproved && !workarea.isOnHold
              return {
                value: workarea.id,
                label: `${workarea.name}${!isApproved ? " (Not Approved)" : ""}`,
                disabled: !isApproved,
                scheduledEventCount: workarea.scheduledEventCount,
              }
            })}
            optionRender={(option) => (
              <Space>
                <span>{option.label}</span>
                {option.data.scheduledEventCount === 0 ? (
                  <Tag color="error" bordered={true} style={{ fontSize: "11px", margin: 0 }}>
                    Unscheduled
                  </Tag>
                ) : (
                  <Tag bordered={true} style={{ fontSize: "11px", margin: 0 }}>
                    Scheduled
                  </Tag>
                )}
              </Space>
            )}
            value={selectedWorkareaIds}
            onChange={(values) => setFormValues({ selectedWorkareaIds: values })}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
          {showSelectAllButtons && (
            <Flex gap="small" style={{ marginTop: "8px", marginBottom: "16px" }}>
              <Button onClick={() => setFormValues({ selectedWorkareaIds: allWorkareas.map((w) => w.id) })}>
                Select All
              </Button>
              <Button onClick={() => setFormValues({ selectedWorkareaIds: [] })}>
                Unselect All
              </Button>
            </Flex>
          )}
          {assignedWorkareas.length > 0 ? (
            assignedWorkareas.map((assignedWorkarea) => (
              <Card
                key={assignedWorkarea.id}
                size="small"
                style={{
                  width: "100%",
                  marginTop: 4,
                  marginBottom: 4,
                  background: "#fafafa",
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <Space>
                    <Text strong style={{ fontSize: 14 }}>
                      {assignedWorkarea.name}
                    </Text>
                    {assignedWorkarea.scheduledEventCount === 0 ? (
                      <Tag color="error" bordered={true} style={{ fontSize: "11px", margin: 0 }}>
                        Unscheduled
                      </Tag>
                    ) : (
                      <Tag bordered={true} style={{ fontSize: "11px", margin: 0 }}>
                        Scheduled
                      </Tag>
                    )}
                  </Space>
                  <CloseCircleOutlined
                    style={{ color: "#888", fontSize: 14, cursor: "pointer" }}
                    onClick={() => removeWorkarea(assignedWorkarea.id)}
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    rowGap: 4,
                    fontSize: 12,
                    color: "#888",
                  }}
                >
                  <span>
                    Estimated Manhours{" "}
                    <Text strong style={{ fontSize: 12 }}>
                      {assignedWorkarea.estimatedHours} Hours
                    </Text>
                  </span>
                  <span style={{ textAlign: "right" }}>
                    Pricing Type{" "}
                    <Text strong style={{ fontSize: 12 }}>
                      {pricingTypeName(assignedWorkarea.pricingType)}
                    </Text>
                  </span>
                  {isWorkareaRecurringEnabled(assignedWorkarea.recurringScheduleConfig) ? (
                    <span>
                      Repeats{" "}
                      <Text strong style={{ fontSize: 12 }}>
                        {workareaRepeatLabel(assignedWorkarea.recurringScheduleConfig!)}
                      </Text>
                      {!hideRepeatSelector && (
                        <a
                          onClick={() =>
                            applyWorkareaSchedule(assignedWorkarea.recurringScheduleConfig!)
                          }
                          style={{ color: "#d4380d", marginLeft: 8, fontSize: 12 }}
                        >
                          Set as Schedule
                        </a>
                      )}
                    </span>
                  ) : (
                    <span />
                  )}
                  <span style={{ textAlign: "right" }}>
                    Approved Visits{" "}
                    <Text strong style={{ fontSize: 12 }}>
                      {assignedWorkarea.estimatedOccurrences ?? 0}
                    </Text>
                  </span>
                </div>
              </Card>
            ))
          ) : (
            <Flex
              vertical
              justify="center"
              align="center"
              style={{
                flex: 1,
                border: "1px dashed #d9d9d9",
                borderRadius: "8px",
                padding: "16px",
                maxHeight: "120px",
              }}
            >
              <Text type="secondary">No Assigned Work Areas</Text>
            </Flex>
          )}
        </Card>
      </div>

      <EventFormScheduleFields
        formState={formState}
        hideRepeatSelector={hideRepeatSelector}
      />
      <EventFormCrewFields formState={formState} showNotificationOption />

      <div style={{ marginBottom: "24px" }}>
        <Checkbox
          checked={formValues.frozen}
          onChange={(e) => setFormValues({ frozen: e.target.checked })}
        >
          This event is frozen and cannot be rescheduled
        </Checkbox>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Text style={{ fontSize: "14px", display: "block", marginBottom: "8px" }}>
          Notes
        </Text>
        <Input.TextArea
          rows={3}
          placeholder="Add notes for this event..."
          value={formValues.notes}
          onChange={(e) => setFormValues({ notes: e.target.value })}
        />
      </div>
    </div>
  )
}

export default EventFormFields
